/**
 * The arithmetic behind the performance dashboard.
 *
 * The book's line for this module is a constraint, not a flourish: "Ratings are
 * a distribution, not a score, and no individual is ever coloured by their
 * rating." So the categorical palette is used on the distribution columns —
 * the one place in PayLoom it earns its place — and nothing in here returns a
 * colour for a person.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "10 Apr" — spelled from a table, because ICU renders "Sept" for September. */
export const shortDate = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  let mo, d;
  if (m) {
    [, , mo, d] = m;
  } else {
    const parsed = new Date(s);
    if (Number.isNaN(parsed.getTime())) return null;
    mo = parsed.getUTCMonth() + 1;
    d = parsed.getUTCDate();
  }
  const i = Number(mo) - 1;
  if (i < 0 || i > 11) return null;
  return `${Number(d)} ${MONTHS[i]}`;
};

/**
 * The rating bands, taken from the cycle's own scale.
 *
 * The book labels five bands Below / Developing / Meets / Exceeds /
 * Outstanding, but those are one organisation's words. `ratingScale.labels` is
 * where a cycle states its own, and `max` is where it states how many there
 * are — a four-point scale is a legitimate thing to configure. Hardcoding the
 * book's five would mislabel every cycle that does not use them.
 */
export const ratingBands = (cycle) => {
  const max = Number(cycle?.ratingScale?.max) || 5;
  const labels = cycle?.ratingScale?.labels;
  return Array.from({ length: max }, (_, i) => ({
    score: i + 1,
    label: Array.isArray(labels) && labels[i] ? labels[i] : String(i + 1),
  }));
};

/**
 * The distribution across those bands. A band with nobody in it still appears —
 * a gap in a distribution is information, and dropping empty columns turns five
 * bands into three and hides that nobody was rated Below.
 */
export const distribution = (ratingDistribution = [], bands = []) => {
  const counts = new Map(
    (ratingDistribution || []).map((r) => [Number(r.rating), Number(r.count) || 0])
  );
  const rows = bands.map((b, i) => ({
    ...b,
    count: counts.get(b.score) || 0,
    colorIndex: i,
  }));
  const total = rows.reduce((t, r) => t + r.count, 0);
  const max = Math.max(0, ...rows.map((r) => r.count));
  return { rows, total, max };
};

/**
 * Cycle progress.
 *
 * The book asks for four stages. Two of them — peer feedback given, and
 * calibration signed off — have nothing behind them: there is no peer review
 * and no calibration anywhere in the data model, and a progress bar invented
 * from no data is worse than an absent one, because it reads as a measurement.
 * So this returns only the stages that are real, and says which are missing so
 * the screen can be honest about it rather than silently short.
 */
export const MISSING_STAGES = [
  { key: "peerFeedback", label: "Peer feedback given" },
  { key: "calibration", label: "Calibration signed off" },
];

export const cycleProgress = (funnel = {}) => {
  const total = Number(funnel.total) || 0;
  const pending = Number(funnel.pending) || 0;
  const selfDone = Number(funnel.self_completed) || 0;
  const managerDone = Number(funnel.manager_completed) || 0;
  const finalized = Number(funnel.finalized) || 0;

  // Each stage counts everyone who has reached it *or passed it*: a finalized
  // review is also a submitted self-review, and counting only the current
  // bucket would make progress appear to go backwards as work completes.
  return {
    total,
    awaitingSelf: pending,
    stages: [
      { key: "self", label: "Self-review submitted", done: selfDone + managerDone + finalized, of: total },
      { key: "manager", label: "Manager review complete", done: managerDone + finalized, of: total },
      { key: "finalized", label: "Review finalised", done: finalized, of: total },
    ],
  };
};

export const share = (done, of) => (of > 0 ? done / of : 0);

/**
 * Whose turn it is on a review the signed-in manager owns.
 *
 * Three states, and the pill wording is about the reader: "Your turn" is the
 * only one that is an instruction. The rest describe where the review is.
 */
export const reportState = (review) => {
  const status = review?.status;
  if (status === "finalized") return { key: "complete", label: "Complete", tone: "success" };
  if (status === "manager_completed") return { key: "complete", label: "Awaiting sign-off", tone: "info" };
  if (status === "self_completed") return { key: "yours", label: "Your turn", tone: "warning" };
  return { key: "waiting", label: "Waiting", tone: "muted" };
};

/** The line under the name, describing where the review actually is. */
export const reportDetail = (review) => {
  switch (review?.status) {
    case "finalized":
      return review?.overallRating != null
        ? `Rating recorded · ${review.overallRating}`
        : "Rating recorded";
    case "manager_completed":
      return "Your review is in · awaiting sign-off";
    case "self_completed":
      return "Self-review in · yours pending";
    default:
      return "Awaiting self-review";
  }
};

/**
 * "3 of 4 goals met". Only goals tied to this cycle count — a goal from last
 * year is not evidence about this review.
 */
export const goalsMet = (goals = [], employeeId, cycleId) => {
  const mine = (goals || []).filter((g) => {
    const owner = g?.employee?._id || g?.employee;
    if (String(owner) !== String(employeeId)) return false;
    if (!cycleId) return true;
    const cycle = g?.reviewCycle?._id || g?.reviewCycle;
    return String(cycle) === String(cycleId);
  });
  return { met: mine.filter((g) => g.status === "completed").length, of: mine.length };
};

/** How many of the manager's reports are done, for the section's own caption. */
export const reportsComplete = (reviews = []) => ({
  done: reviews.filter((r) => reportState(r).key === "complete").length,
  of: reviews.length,
});

/**
 * The cycle the dashboard should open on: the active one, else the most
 * recently activated. A dashboard that opens on a draft cycle shows an empty
 * distribution and reads as a broken screen rather than an unstarted one.
 */
export const currentCycle = (cycles = []) => {
  const active = cycles.filter((c) => c.status === "active");
  if (active.length) {
    return [...active].sort(
      (a, b) => Date.parse(b.activatedAt || 0) - Date.parse(a.activatedAt || 0)
    )[0];
  }
  return (
    [...cycles].sort((a, b) => Date.parse(b.activatedAt || b.createdAt || 0) - Date.parse(a.activatedAt || a.createdAt || 0))[0] || null
  );
};

/**
 * The deadline line under the cycle name. The book shows two dates — self
 * reviews close, manager reviews close — where a cycle carries one `dueDate`.
 * Rather than inventing a second deadline by offsetting the first, this states
 * the one date that exists.
 */
export const deadlineLine = (cycle) => {
  const due = shortDate(cycle?.dueDate);
  if (!due) return null;
  return `Reviews close ${due}`;
};
